import React, { useState, useEffect, useRef, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { Button } from "@/components/Button";
import { Textarea } from "@/components/Textarea";
import { DatePicker } from "@/components/DatePicker";
import { Accordion } from "@/components/Accordion";
import { useToast } from "@/context/ToastContext";
import { sprintsApi, tasksApi, taskStatusesApi } from "@/lib/api";
import { NewTaskModal } from "@/pages/Tasks/NewTaskModal";
import { TaskViewModal } from "@/pages/Tasks/TaskViewModal";
import { ChangeTaskPriorityModal } from "@/pages/Tasks/ChangeTaskPriorityModal";
import { ChangeTaskStatusModal } from "@/pages/Tasks/ChangeTaskStatusModal";
import { ChangeTaskAssigneeModal } from "@/pages/Tasks/ChangeTaskAssigneeModal";
import { Badge, type BadgeVariant } from "@/components/Badge";
import { Tooltip } from "@/components/Tooltip";
import { formatDate } from "@/utils/format";
import { PRIORITY_CONFIG } from "@/components/PrioritySelect";
import { Switch } from "@/components/Switch";
import { toDateStr, today as todayStr, getAvatar } from "@/utils/format";
import { PageHeader } from "@/components/PageHeader";

export interface Sprint {
    id: number;
    name: string;
    goal?: string | null;
    status: "planning" | "active" | "completed";
    start_date?: string | null;
    end_date?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    order: number;
    tasks_count?: number;
}

export interface TaskItem {
    id: number;
    code: string;
    title: string;
    description?: string | null;
    sprint_id?: number | null;
    status_id: number;
    status?: {
        id: number;
        name: string;
        tag?: string;
        color?: string;
    };
    priority?: number | string;
    assignee?: {
        id: number;
        name: string;
        avatar?: string;
        photo?: string;
    } | null;
}

export interface SprintsSettingsProps {
    onSprintChanged?: () => void;
}

const TASK_STATUS_VARIANT: Record<string, BadgeVariant> = {
    todo: "neutral",
    progress: "info",
    review: "warning",
    done: "success",
    cancelled: "danger",
};

interface MoveTaskDropdownProps {
    options: { value: string; label: string }[];
    onSelect: (value: number | null) => void;
}

const MoveTaskDropdown: React.FC<MoveTaskDropdownProps> = ({
    options,
    onSelect,
}) => {
    const { t } = useTranslation("global");
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const filteredOptions = options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelectOption = (value: string): void => {
        setIsOpen(false);
        setSearch("");
        onSelect(value === "backlog" ? null : parseInt(value));
    };

    return (
        <div
            ref={containerRef}
            className="relative shrink-0"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <Button
                variant="custom"
                size="none"
                icon="arrowUpDown"
                tooltip={t("tasks.sprints.move")}
                onClick={() => setIsOpen(!isOpen)}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-zinc-700 text-slate-500 hover:text-primary hover:border-primary hover:bg-primary/5 transition-colors shadow-2xs bg-white dark:bg-zinc-900"
            />

            {isOpen && (
                <div className="absolute left-0 top-full mt-1.5 w-56 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-xl shadow-lg z-[120000] overflow-hidden py-1.5 select-dropdown--open-bottom origin-top">
                    <div className="p-2 border-b border-slate-100 dark:border-zinc-800">
                        <div className="relative">
                            <Icon
                                value="search"
                                size={12}
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t("tasks.sprints.search_sprint")}
                                autoFocus
                                className="w-full pl-7 pr-2.5 py-1 text-xs bg-slate-50 dark:bg-zinc-800 rounded-lg border-0 outline-none text-slate-800 dark:text-zinc-100 placeholder:text-slate-400"
                            />
                        </div>
                    </div>

                    <div className="max-h-48 overflow-y-auto py-1">
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-2 text-center text-xs text-slate-400 dark:text-zinc-500">
                                {t("tasks.sprints.no_future")}
                            </div>
                        ) : (
                            filteredOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() =>
                                        handleSelectOption(opt.value)
                                    }
                                    className="w-full px-3 py-1.5 text-left text-xs text-slate-700 dark:text-zinc-200 hover:bg-primary/10 hover:text-primary flex items-center gap-2 transition-colors cursor-pointer"
                                >
                                    <Icon
                                        value={
                                            opt.value === "backlog"
                                                ? "inbox"
                                                : "inbox"
                                        }
                                        size={14}
                                        className="text-slate-400 shrink-0"
                                    />
                                    <span className="truncate">
                                        {opt.label}
                                    </span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const SprintActionsPopover: React.FC<{
    sprint: Sprint;
    onEdit: (sprint: Sprint) => void;
    onDelete?: (sprint: Sprint) => void;
    onMoveTasks?: (sprint: Sprint) => void;
    isMoving?: boolean;
}> = ({ sprint, onEdit, onDelete, onMoveTasks, isMoving = false }) => {
    const { t } = useTranslation("global");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    return (
        <div
            ref={containerRef}
            className="relative shrink-0 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <Button
                variant="basic"
                icon="moreVertical"
                onClick={() => setIsOpen(!isOpen)}
                className="text-slate-400! w-9 h-9"
            />

            {isOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-40 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-xl shadow-lg z-[120000] overflow-hidden py-1.5 select-dropdown--open-bottom origin-top">
                    {onMoveTasks && (
                        <button
                            type="button"
                            onClick={() => {
                                setIsOpen(false);
                                onMoveTasks(sprint);
                            }}
                            className="w-full px-3 py-2 text-left text-xs font-medium text-slate-700 dark:text-zinc-200 hover:bg-slate-50 dark:hover:bg-zinc-800 flex items-center gap-2 transition-colors cursor-pointer"
                        >
                            <Icon
                                value={isMoving ? "x" : "arrowUpDown"}
                                size={14}
                                className={
                                    isMoving
                                        ? "text-rose-500"
                                        : "text-slate-400"
                                }
                            />
                            {isMoving
                                ? t("tasks.sprints.cancel_move_tasks")
                                : t("tasks.sprints.move_tasks")}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            setIsOpen(false);
                            onEdit(sprint);
                        }}
                        className="w-full px-3 py-2 text-left text-xs font-medium text-slate-700 dark:text-zinc-200 hover:bg-slate-50 dark:hover:bg-zinc-800 flex items-center gap-2 transition-colors cursor-pointer"
                    >
                        <Icon
                            value="edit"
                            size={14}
                            className="text-slate-400"
                        />
                        {t("tasks.sprints.edit")}
                    </button>
                    {onDelete && (
                        <button
                            type="button"
                            onClick={() => {
                                setIsOpen(false);
                                onDelete(sprint);
                            }}
                            className="w-full px-3 py-2 text-left text-xs font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center gap-2 transition-colors cursor-pointer"
                        >
                            <Icon
                                value="trash"
                                size={14}
                                className="text-rose-500"
                            />
                            {t("tasks.sprints.delete")}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const BacklogActionsPopover: React.FC<{
    onMoveTasks: () => void;
    isMoving?: boolean;
}> = ({ onMoveTasks, isMoving = false }) => {
    const { t } = useTranslation("global");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    return (
        <div
            ref={containerRef}
            className="relative shrink-0 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <Button
                variant="basic"
                icon="moreVertical"
                onClick={() => setIsOpen(!isOpen)}
                className="text-slate-400! w-9 h-9"
            />

            {isOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-40 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-xl shadow-lg z-[120000] overflow-hidden py-1.5 select-dropdown--open-bottom origin-top">
                    <button
                        type="button"
                        onClick={() => {
                            setIsOpen(false);
                            onMoveTasks();
                        }}
                        className="w-full px-3 py-2 text-left text-xs font-medium text-slate-700 dark:text-zinc-200 hover:bg-slate-50 dark:hover:bg-zinc-800 flex items-center gap-2 transition-colors cursor-pointer"
                    >
                        <Icon
                            value={isMoving ? "x" : "arrowUpDown"}
                            size={14}
                            className={
                                isMoving ? "text-rose-500" : "text-slate-400"
                            }
                        />
                        {isMoving
                            ? t("tasks.sprints.cancel_move_tasks")
                            : t("tasks.sprints.move_tasks")}
                    </button>
                </div>
            )}
        </div>
    );
};

export const SprintsSettings: React.FC<SprintsSettingsProps> = ({
    onSprintChanged,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [sprints, setSprints] = useState<Sprint[]>([]);
    const [planningOrder, setPlanningOrder] = useState<number[]>([]);
    const [tasks, setTasks] = useState<TaskItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
    const [selectedTask, setSelectedTask] = useState<TaskItem | null>(null);
    const [moveModeSection, setMoveModeSection] = useState<
        number | "backlog" | null
    >(null);
    const [expandedSections, setExpandedSections] = useState<
        Record<string | number, boolean>
    >({});
    const [isCompletingHover, setIsCompletingHover] = useState<boolean>(false);
    const [taskOrders, setTaskOrders] = useState<Map<string, number[]>>(
        new Map()
    );
    const [searchQuery, setSearchQuery] = useState<string>("");

    // Modal states
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [editingSprint, setEditingSprint] = useState<Sprint | null>(null);
    const [sprintFormData, setSprintFormData] = useState<{
        name: string;
        goal: string;
        start_date: string;
        end_date: string;
    }>({
        name: "",
        goal: "",
        start_date: "",
        end_date: "",
    });

    const [startSprintTarget, setStartSprintTarget] = useState<Sprint | null>(
        null
    );
    const [startFormData, setStartFormData] = useState<{
        start_date: string;
        end_date: string;
        goal: string;
    }>({
        start_date: "",
        end_date: "",
        goal: "",
    });
    const [startFormErrors, setStartFormErrors] = useState<{
        start_date?: string;
        end_date?: string;
    }>({});

    const [completeSprintTarget, setCompleteSprintTarget] =
        useState<Sprint | null>(null);
    const [moveToSprintId, setMoveToSprintId] = useState<string>("backlog");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [sprintToDelete, setSprintToDelete] = useState<Sprint | null>(null);

    const [statuses, setStatuses] = useState<any[]>([]);
    const [isNewTaskModalOpen, setIsNewTaskModalOpen] =
        useState<boolean>(false);
    const [createTaskSprintId, setCreateTaskSprintId] = useState<number | null>(
        null
    );
    const [priorityTarget, setPriorityTarget] = useState<TaskItem | null>(null);
    const [statusTarget, setStatusTarget] = useState<TaskItem | null>(null);
    const [assigneeTarget, setAssigneeTarget] = useState<TaskItem | null>(null);

    const handleTaskAttributeUpdated = (updated: any): void => {
        setTasks((prev) =>
            prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t))
        );
        loadData(false);
        if (onSprintChanged) {
            onSprintChanged();
        }
    };

    const handleOpenCreateTask = (sprintId: number | null): void => {
        setCreateTaskSprintId(sprintId);
        setIsNewTaskModalOpen(true);
    };

    const handleTaskCreated = (newTask: any): void => {
        setTasks((prev) => [...prev, newTask]);
        if (onSprintChanged) {
            onSprintChanged();
        }
    };

    useEffect(() => {
        loadData(true);
    }, []);

    const loadData = async (showLoading = false): Promise<void> => {
        if (showLoading) {
            setLoading(true);
        }
        try {
            const [sprintsRes, tasksRes, statusesRes] = await Promise.all([
                sprintsApi.list(),
                tasksApi.list({ order_by: "sprint_order" }),
                taskStatusesApi.list(),
            ]);

            if (sprintsRes.result) {
                setSprints(sprintsRes.data);
                const planning = (sprintsRes.data as Sprint[])
                    .filter((s: Sprint) => s.status === "planning")
                    .sort(
                        (a: Sprint, b: Sprint) =>
                            (a.order ?? 0) - (b.order ?? 0)
                    );
                setPlanningOrder(planning.map((s: Sprint) => s.id));
            }
            if (tasksRes.result) {
                const loadedTasks: Task[] = tasksRes.data;
                setTasks(loadedTasks);

                // Build taskOrders map keyed by sectionKey ('backlog' or sprint_id)
                const ordersMap = new Map<string, number[]>();
                const sorted = [...loadedTasks].sort((a, b) => {
                    const oA = a.sprint_order ?? 0;
                    const oB = b.sprint_order ?? 0;
                    if (oA !== oB) {
                        return oA - oB;
                    }
                    return (
                        (a.sort_order ?? 0) - (b.sort_order ?? 0) || b.id - a.id
                    );
                });
                sorted.forEach((task) => {
                    const key =
                        task.sprint_id === null || task.sprint_id === undefined
                            ? "backlog"
                            : String(task.sprint_id);
                    if (!ordersMap.has(key)) {
                        ordersMap.set(key, []);
                    }
                    ordersMap.get(key)!.push(task.id);
                });
                setTaskOrders(ordersMap);
            }
            if (statusesRes.result) {
                setStatuses(statusesRes.data);
            }
        } catch (error) {
            console.error("Error loading sprints data:", error);
            showError(t("tasks.error_loading"));
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    const handleOpenCreateModal = (): void => {
        const nextNum = sprints.length + 1;
        setSprintFormData({
            name: `Sprint ${nextNum}`,
            goal: "",
            start_date: "",
            end_date: "",
        });
        setEditingSprint(null);
        setIsCreateModalOpen(true);
    };

    const handleOpenEditModal = (sprint: Sprint): void => {
        setEditingSprint(sprint);
        setSprintFormData({
            name: sprint.name,
            goal: sprint.goal || "",
            start_date: toDateStr(sprint.start_date),
            end_date: toDateStr(sprint.end_date),
        });
        setIsCreateModalOpen(true);
    };

    const handleSaveSprint = async (): Promise<void> => {
        if (!sprintFormData.name.trim()) {
            return;
        }

        if (sprintFormData.end_date && !sprintFormData.start_date) {
            showError(t("tasks.sprints.end_date_no_start"));
            return;
        }

        if (
            sprintFormData.start_date &&
            sprintFormData.end_date &&
            sprintFormData.end_date < sprintFormData.start_date
        ) {
            showError(t("tasks.sprints.end_date_before_start"));
            return;
        }

        setIsSubmitting(true);
        try {
            if (editingSprint) {
                const res = await sprintsApi.update(
                    editingSprint.id,
                    sprintFormData
                );
                if (res.result) {
                    setSprints((prev) =>
                        prev.map((s) =>
                            s.id === editingSprint.id ? res.data : s
                        )
                    );
                    showSuccess(t("tasks.sprints.edit"));
                }
            } else {
                const res = await sprintsApi.create(sprintFormData);
                if (res.result) {
                    setSprints((prev) => [...prev, res.data]);
                    setPlanningOrder((prev) => [...prev, res.data.id]);
                    showSuccess(t("tasks.sprints.created_success"));
                }
            }

            setIsCreateModalOpen(false);
            if (onSprintChanged) {
                onSprintChanged();
            }
        } catch (error) {
            console.error("Error saving sprint:", error);
            showError(t("tasks.error_loading"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteSprint = (sprint: Sprint): void => {
        setSprintToDelete(sprint);
    };

    const confirmDeleteSprint = async (): Promise<void> => {
        if (!sprintToDelete) return;

        try {
            await sprintsApi.delete(sprintToDelete.id);
            setSprints((prev) =>
                prev.filter((s) => s.id !== sprintToDelete.id)
            );
            setTasks((prev) =>
                prev.map((t) =>
                    t.sprint_id === sprintToDelete.id
                        ? { ...t, sprint_id: null }
                        : t
                )
            );
            showSuccess(t("tasks.sprints.delete"));
            if (onSprintChanged) {
                onSprintChanged();
            }
        } catch (error) {
            console.error("Error deleting sprint:", error);
            showError(t("tasks.error_loading"));
        } finally {
            setSprintToDelete(null);
        }
    };

    const handleOpenStartModal = (sprint: Sprint): void => {
        const existingActive = sprints.find((s) => s.status === "active");
        if (existingActive && existingActive.id !== sprint.id) {
            showError(
                t("tasks.sprints.already_active_error", {
                    name: existingActive.name,
                })
            );
            return;
        }

        const twoWeeksLater = toDateStr(
            new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
        );

        setStartSprintTarget(sprint);
        setStartFormErrors({});
        setStartFormData({
            start_date: toDateStr(sprint.start_date) || todayStr(),
            end_date: toDateStr(sprint.end_date) || twoWeeksLater,
            goal: sprint.goal || "",
        });
    };

    const handleConfirmStart = async (): Promise<void> => {
        if (!startSprintTarget) {
            return;
        }

        const existingActive = sprints.find((s) => s.status === "active");
        if (existingActive && existingActive.id !== startSprintTarget.id) {
            showError(
                t("tasks.sprints.already_active_error", {
                    name: existingActive.name,
                })
            );
            return;
        }

        // Frontend validation
        const errors: { start_date?: string; end_date?: string } = {};
        if (!startFormData.start_date) {
            errors.start_date = t(
                "tasks.sprints.validation.start_date_required"
            );
        }
        if (!startFormData.end_date) {
            errors.end_date = t("tasks.sprints.validation.end_date_required");
        }
        if (Object.keys(errors).length > 0) {
            setStartFormErrors(errors);
            return;
        }
        if (startFormData.end_date < startFormData.start_date) {
            setStartFormErrors((prev) => ({ ...prev, end_date: " " }));
            showError(t("tasks.sprints.end_date_before_start"));
            return;
        }
        setStartFormErrors({});

        setIsSubmitting(true);
        try {
            // Sanitize empty strings to null so Laravel date validator doesn't fail
            const payload = {
                start_date: startFormData.start_date || null,
                end_date: startFormData.end_date || null,
                goal: startFormData.goal || null,
            };
            const res = await sprintsApi.start(startSprintTarget.id, payload);
            if (res.result) {
                showSuccess(t("tasks.sprints.started_success"));
                setStartSprintTarget(null);
                await loadData();
                if (onSprintChanged) {
                    onSprintChanged();
                }
            }
        } catch (error: any) {
            console.error("Error starting sprint:", error);
            showError(error?.message || t("tasks.error_loading"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenCompleteModal = (sprint: Sprint): void => {
        setCompleteSprintTarget(sprint);
        setMoveToSprintId("backlog");
    };

    const handleConfirmComplete = async (): Promise<void> => {
        if (!completeSprintTarget) {
            return;
        }

        setIsSubmitting(true);
        try {
            const targetSprintNum =
                moveToSprintId === "backlog" ? null : parseInt(moveToSprintId);
            const res = await sprintsApi.complete(completeSprintTarget.id, {
                move_to_sprint_id: targetSprintNum,
            });

            if (res.result) {
                showSuccess(t("tasks.sprints.complete"));
                setCompleteSprintTarget(null);
                loadData();
                if (onSprintChanged) {
                    onSprintChanged();
                }
            }
        } catch (error) {
            showError(t("tasks.error_loading"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const getSectionTasks = useCallback(
        (sprintId: number | null): TaskItem[] => {
            const key = sprintId === null ? "backlog" : String(sprintId);
            const orderedIds = taskOrders.get(key);

            const filtered =
                sprintId === null
                    ? tasks.filter((t) => !t.sprint_id)
                    : tasks.filter((t) => t.sprint_id === sprintId);

            if (!orderedIds || orderedIds.length === 0) {
                return [...filtered].sort((a, b) => {
                    const orderA = a.sprint_order ?? 0;
                    const orderB = b.sprint_order ?? 0;
                    if (orderA !== orderB) {
                        return orderA - orderB;
                    }
                    return (
                        (a.sort_order ?? 0) - (b.sort_order ?? 0) || b.id - a.id
                    );
                });
            }

            // Use stable order from taskOrders map
            const taskMap = new Map(filtered.map((t) => [t.id, t]));
            const ordered: TaskItem[] = [];
            orderedIds.forEach((id) => {
                const task = taskMap.get(id);
                if (task) {
                    ordered.push(task);
                }
            });
            // Append any tasks not yet in the order map (e.g. newly created)
            filtered.forEach((t) => {
                if (!orderedIds.includes(t.id)) {
                    ordered.push(t);
                }
            });
            return ordered;
        },
        [tasks, taskOrders]
    );

    const matchesSearch = useCallback(
        (task: TaskItem): boolean => {
            if (!searchQuery.trim()) {
                return true;
            }

            const q = searchQuery.toLowerCase().trim();
            const code = task.code?.toLowerCase() || "";
            const title = task.title?.toLowerCase() || "";
            const desc = task.description?.toLowerCase() || "";
            const assignee = task.assignee?.name?.toLowerCase() || "";
            return (
                code.includes(q) ||
                title.includes(q) ||
                desc.includes(q) ||
                assignee.includes(q)
            );
        },
        [searchQuery]
    );

    const getFilteredSectionTasks = useCallback(
        (sprintId: number | null): TaskItem[] => {
            const list = getSectionTasks(sprintId);
            if (!searchQuery.trim()) {
                return list;
            }

            return list.filter(matchesSearch);
        },
        [getSectionTasks, matchesSearch, searchQuery]
    );

    const handleSprintDragEnd = async (result: any): Promise<void> => {
        const { source, destination, draggableId } = result;
        if (!destination || source.index === destination.index) {
            return;
        }

        const sprintId = parseInt(draggableId.replace("sprint-", ""));
        const newOrder = Array.from(planningOrder);
        newOrder.splice(source.index, 1);
        newOrder.splice(destination.index, 0, sprintId);
        setPlanningOrder(newOrder);

        const orderMap = new Map(newOrder.map((id, idx) => [id, idx + 1]));
        setSprints((prev) =>
            prev.map((s) => {
                const ord = orderMap.get(s.id);
                if (ord !== undefined) {
                    return { ...s, order: ord };
                }
                return s;
            })
        );

        try {
            await sprintsApi.reorder(
                newOrder.map((id, idx) => ({ id, order: idx + 1 }))
            );
            if (onSprintChanged) {
                onSprintChanged();
            }
        } catch (error) {
            console.error("Error reordering sprints:", error);
            showError(t("tasks.error_reorder"));
            loadData();
        }
    };

    const handleUnifiedDragEnd = async (result: any): Promise<void> => {
        if (!result.destination) {
            return;
        }

        if (result.type === "sprint") {
            await handleSprintDragEnd(result);
        } else {
            await handleDragEnd(result);
        }
    };

    const handleDragEnd = async (result: any): Promise<void> => {
        const { source, destination, draggableId } = result;
        if (!destination) {
            return;
        }

        const taskId = parseInt(draggableId);
        const sourceSectionId =
            source.droppableId === "backlog"
                ? null
                : parseInt(source.droppableId);
        const targetSectionId =
            destination.droppableId === "backlog"
                ? null
                : parseInt(destination.droppableId);

        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        if (source.droppableId === destination.droppableId) {
            const currentList = Array.from(getSectionTasks(sourceSectionId));
            const movedIndex = currentList.findIndex((t) => t.id === taskId);
            if (movedIndex === -1) {
                return;
            }

            const [movedItem] = currentList.splice(movedIndex, 1);

            if (!searchQuery.trim()) {
                currentList.splice(destination.index, 0, movedItem);
            } else {
                const filteredList = getFilteredSectionTasks(
                    sourceSectionId
                ).filter((t) => t.id !== taskId);
                if (
                    filteredList.length === 0 ||
                    destination.index >= filteredList.length
                ) {
                    currentList.push(movedItem);
                } else {
                    const targetNeighbor = filteredList[destination.index];
                    const neighborIdx = currentList.findIndex(
                        (t) => t.id === targetNeighbor.id
                    );
                    if (neighborIdx !== -1) {
                        currentList.splice(neighborIdx, 0, movedItem);
                    } else {
                        currentList.splice(destination.index, 0, movedItem);
                    }
                }
            }

            const reorderedPayload = currentList.map((t, idx) => ({
                id: t.id,
                sprint_order: idx + 1,
            }));

            // Update taskOrders map immediately (no glitch)
            const sectionKey =
                source.droppableId === "backlog"
                    ? "backlog"
                    : source.droppableId;
            const newOrderIds = currentList.map((t) => t.id);
            setTaskOrders((prev) => {
                const next = new Map(prev);
                next.set(sectionKey, newOrderIds);
                return next;
            });

            // Also update sprint_order in tasks state for consistency
            const orderMap = new Map(
                reorderedPayload.map((item) => [item.id, item.sprint_order])
            );
            setTasks((prev) =>
                prev.map((t) => {
                    const newOrder = orderMap.get(t.id);
                    if (newOrder !== undefined) {
                        return { ...t, sprint_order: newOrder };
                    }
                    return t;
                })
            );

            try {
                await tasksApi.reorder({ tasks: reorderedPayload });
                if (onSprintChanged) {
                    onSprintChanged();
                }
            } catch (error) {
                console.error("Error reordering tasks:", error);
                showError(t("tasks.error_reorder"));
                loadData();
            }
        } else {
            const sourceList = Array.from(getSectionTasks(sourceSectionId));
            const targetList = Array.from(getSectionTasks(targetSectionId));

            const movedIndex = sourceList.findIndex((t) => t.id === taskId);
            if (movedIndex === -1) {
                return;
            }

            const [movedItem] = sourceList.splice(movedIndex, 1);
            const updatedMovedItem = {
                ...movedItem,
                sprint_id: targetSectionId,
            };

            if (!searchQuery.trim()) {
                targetList.splice(destination.index, 0, updatedMovedItem);
            } else {
                const filteredTargetList =
                    getFilteredSectionTasks(targetSectionId);
                if (
                    filteredTargetList.length === 0 ||
                    destination.index >= filteredTargetList.length
                ) {
                    targetList.push(updatedMovedItem);
                } else {
                    const targetNeighbor =
                        filteredTargetList[destination.index];
                    const neighborIdx = targetList.findIndex(
                        (t) => t.id === targetNeighbor.id
                    );
                    if (neighborIdx !== -1) {
                        targetList.splice(neighborIdx, 0, updatedMovedItem);
                    } else {
                        targetList.splice(
                            destination.index,
                            0,
                            updatedMovedItem
                        );
                    }
                }
            }

            const targetPayload = targetList.map((t, idx) => ({
                id: t.id,
                sprint_id: targetSectionId,
                sprint_order: idx + 1,
            }));

            const targetMap = new Map(
                targetPayload.map((item) => [item.id, item])
            );
            setTasks((prev) =>
                prev.map((t) => {
                    const updated = targetMap.get(t.id);
                    if (updated) {
                        return {
                            ...t,
                            sprint_id: updated.sprint_id,
                            sprint_order: updated.sprint_order,
                        };
                    }
                    return t;
                })
            );

            // Update order maps for both source and target sections
            const sourceKey =
                source.droppableId === "backlog"
                    ? "backlog"
                    : source.droppableId;
            const targetKey =
                destination.droppableId === "backlog"
                    ? "backlog"
                    : destination.droppableId;
            setTaskOrders((prev) => {
                const next = new Map(prev);
                next.set(
                    sourceKey,
                    sourceList.map((t) => t.id)
                );
                next.set(
                    targetKey,
                    targetList.map((t) => t.id)
                );
                return next;
            });

            try {
                await tasksApi.reorder({ tasks: targetPayload });
                if (onSprintChanged) {
                    onSprintChanged();
                }
            } catch (error) {
                console.error("Error moving task across sprints:", error);
                showError(t("tasks.error_reorder"));
                loadData();
            }
        }
    };

    const handleMoveTaskDirect = async (
        taskId: number,
        newSprintId: number | null
    ): Promise<void> => {
        setTasks((prev) =>
            prev.map((t) =>
                t.id === taskId ? { ...t, sprint_id: newSprintId } : t
            )
        );

        try {
            await sprintsApi.moveTasks([taskId], newSprintId);
            if (onSprintChanged) {
                onSprintChanged();
            }
        } catch (error) {
            showError(t("tasks.error_reorder"));
            loadData();
        }
    };

    const handleBulkMove = async (
        taskIds: number[],
        newSprintIdVal: string | number
    ): Promise<void> => {
        if (taskIds.length === 0) {
            return;
        }

        const targetSprintId =
            newSprintIdVal === "backlog"
                ? null
                : parseInt(String(newSprintIdVal));

        setTasks((prev) =>
            prev.map((t) =>
                taskIds.includes(t.id) ? { ...t, sprint_id: targetSprintId } : t
            )
        );
        setSelectedTaskIds((prev) =>
            prev.filter((id) => !taskIds.includes(id))
        );

        try {
            await sprintsApi.moveTasks(taskIds, targetSprintId);
            showSuccess(t("tasks.sprints.tasks_moved"));
            if (onSprintChanged) {
                onSprintChanged();
            }
        } catch (error) {
            showError(t("tasks.error_reorder"));
            loadData();
        }
    };

    const handleToggleSelectTask = (taskId: number): void => {
        setSelectedTaskIds((prev) =>
            prev.includes(taskId)
                ? prev.filter((id) => id !== taskId)
                : [...prev, taskId]
        );
    };

    const handleToggleMoveMode = (section: number | "backlog"): void => {
        if (moveModeSection === section) {
            setMoveModeSection(null);
            setSelectedTaskIds([]);
        } else {
            setMoveModeSection(section);
            setSelectedTaskIds([]);
            setExpandedSections((prev) => ({
                ...prev,
                [section]: true,
            }));
        }
    };

    const activeSprint = sprints.find((s) => s.status === "active");
    const planningSprints = sprints.filter((s) => s.status === "planning");
    const backlogTasks = getFilteredSectionTasks(null);

    const formatDates = (
        start?: string | null,
        end?: string | null
    ): string => {
        if (!start && !end) {
            return "";
        }
        const s = start ? formatDate(start, false) : "";
        const e = end ? formatDate(end, false) : "";
        if (s && e) {
            return `${s} - ${e}`;
        }
        return s || e;
    };

    const otherSprintsOptions = (currentSprintId: number | null) => {
        const options: { value: string; label: string }[] = [];
        if (currentSprintId !== null) {
            options.push({
                value: "backlog",
                label: t("tasks.sprints.backlog"),
            });
        }
        sprints
            .filter((s) => s.id !== currentSprintId && s.status !== "completed")
            .forEach((s) => {
                options.push({ value: String(s.id), label: s.name });
            });
        return options;
    };

    const renderMoveRow = (
        currentSprintId: number | null,
        sectionTasks: TaskItem[]
    ) => {
        const sectionKey =
            currentSprintId === null ? "backlog" : currentSprintId;
        if (moveModeSection !== sectionKey) {
            return null;
        }

        const sectionTaskIds = sectionTasks.map((t) => t.id);
        const selectedIds = sectionTaskIds.filter((id) =>
            selectedTaskIds.includes(id)
        );

        return (
            <div className="flex items-center justify-center py-2.5 mb-3 animate-fadeIn">
                <div className="flex items-center gap-2 w-full max-w-lg">
                    <Select
                        className="w-full flex-1"
                        size="sm"
                        placeholderClassName="text-slate-900 dark:text-white font-semibold"
                        searchable={true}
                        searchPlaceholder={t("tasks.sprints.search_sprint")}
                        placeholder={`${t("tasks.sprints.move_to_sprint")} (${selectedIds.length})`}
                        value=""
                        onChange={(val) => {
                            if (val) {
                                if (selectedIds.length === 0) {
                                    showError(
                                        t("tasks.sprints.select_tasks_first")
                                    );
                                    return;
                                }

                                handleBulkMove(selectedIds, val);
                                setMoveModeSection(null);
                            }
                        }}
                        options={otherSprintsOptions(currentSprintId)}
                    />
                    <Button
                        variant="basic"
                        size="sm"
                        icon="x"
                        tooltip={t("tasks.sprints.cancel_move")}
                        onClick={() => {
                            setMoveModeSection(null);
                            setSelectedTaskIds([]);
                        }}
                        className="w-8 h-8 rounded-xl text-slate-500 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 shrink-0"
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Top Bar */}
            <PageHeader
                title={
                    <span className="flex items-center gap-2">
                        <Icon
                            value="inbox"
                            size={20}
                            className="text-primary"
                        />
                        {t("tasks.sprints.title")}
                    </span>
                }
                subtitle={t("tasks.sprints.subtitle")}
                action={{
                    label: t("tasks.sprints.create"),
                    icon: "plus",
                    onClick: handleOpenCreateModal,
                }}
            />

            {/* Search Input */}
            <div className="w-full">
                <Input
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchQuery(e.target.value)
                    }
                    placeholder={t("tasks.sprints.search_placeholder")}
                    icon={<Icon value="search" size={16} />}
                    endIcon={
                        searchQuery ? (
                            <button
                                type="button"
                                onClick={() => setSearchQuery("")}
                                className="w-5 h-5 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-zinc-200 transition-colors cursor-pointer"
                            >
                                <Icon value="x" size={14} />
                            </button>
                        ) : undefined
                    }
                />
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary"></div>
                </div>
            ) : (
                <DragDropContext onDragEnd={handleUnifiedDragEnd}>
                    {/* Active Sprint Section */}
                    {activeSprint ? (
                        (() => {
                            const sprintTasks = getFilteredSectionTasks(
                                activeSprint.id
                            );
                            return (
                                <Accordion
                                    variant="active"
                                    className="mt-3"
                                    hoverGreen={isCompletingHover}
                                    floatingLabel={
                                        <button
                                            type="button"
                                            onMouseEnter={() =>
                                                setIsCompletingHover(true)
                                            }
                                            onMouseLeave={() =>
                                                setIsCompletingHover(false)
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsCompletingHover(false);
                                                setCompleteSprintTarget(
                                                    activeSprint
                                                );
                                            }}
                                            className={`group/pill flex items-center gap-1 px-3 py-0.5 text-xs font-bold text-white rounded-full whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                                                isCompletingHover
                                                    ? "bg-emerald-500"
                                                    : "bg-primary"
                                            }`}
                                        >
                                            <span className="group-hover/pill:hidden">
                                                {t(
                                                    "tasks.sprints.status_active"
                                                )}
                                            </span>
                                            <span className="hidden group-hover/pill:flex items-center gap-1">
                                                <Icon
                                                    value="checkCircle"
                                                    size={12}
                                                />
                                                {t("tasks.sprints.complete")}
                                            </span>
                                        </button>
                                    }
                                    expanded={
                                        expandedSections[activeSprint.id] !==
                                        undefined
                                            ? expandedSections[activeSprint.id]
                                            : true
                                    }
                                    onChange={(expanded) => {
                                        setExpandedSections((prev) => ({
                                            ...prev,
                                            [activeSprint.id]: expanded,
                                        }));
                                    }}
                                    icon={
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                variant="primary"
                                                count
                                                value={String(
                                                    sprintTasks.length
                                                )}
                                            />
                                            <Icon
                                                value="inbox"
                                                size={18}
                                                className="text-primary"
                                            />
                                        </div>
                                    }
                                    title={
                                        <div className="flex items-center gap-2">
                                            <span>{activeSprint.name}</span>
                                            {activeSprint.goal ? (
                                                <span className="text-xs italic text-slate-500 dark:text-zinc-400 border-l border-slate-300 dark:border-zinc-700 pl-3 font-normal">
                                                    {activeSprint.goal}
                                                </span>
                                            ) : null}
                                        </div>
                                    }
                                    subtitle={
                                        formatDates(
                                            activeSprint.start_date,
                                            activeSprint.end_date
                                        ) ? (
                                            <Badge
                                                variant="secondary"
                                                icon="calendar"
                                                value={formatDates(
                                                    activeSprint.start_date,
                                                    activeSprint.end_date
                                                )}
                                            />
                                        ) : undefined
                                    }
                                    actions={
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="basic"
                                                icon="plus"
                                                tooltip={t(
                                                    "tasks.sprints.add_task"
                                                )}
                                                onClick={() =>
                                                    handleOpenCreateTask(
                                                        activeSprint.id
                                                    )
                                                }
                                                className="text-slate-400! hover:text-primary! w-9 h-9"
                                            />

                                            <SprintActionsPopover
                                                sprint={activeSprint}
                                                onEdit={handleOpenEditModal}
                                                onDelete={handleDeleteSprint}
                                                onMoveTasks={
                                                    sprintTasks.length > 0
                                                        ? () =>
                                                              handleToggleMoveMode(
                                                                  activeSprint.id
                                                              )
                                                        : undefined
                                                }
                                                isMoving={
                                                    moveModeSection ===
                                                    activeSprint.id
                                                }
                                            />
                                        </div>
                                    }
                                >
                                    <Droppable
                                        droppableId={String(activeSprint.id)}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                className={`px-5 pt-2 pb-5 min-h-[90px] transition-colors`}
                                            >
                                                {renderMoveRow(
                                                    activeSprint.id,
                                                    sprintTasks
                                                )}

                                                {sprintTasks.length === 0 ? (
                                                    <div
                                                        onClick={() =>
                                                            handleOpenCreateTask(
                                                                activeSprint.id
                                                            )
                                                        }
                                                        className={`text-center py-6 text-xs border border-dashed rounded-xl transition-colors cursor-pointer group ${snapshot.isDraggingOver ? "border-primary bg-primary/5 text-primary" : "text-slate-400 dark:text-zinc-500 border-slate-200 dark:border-zinc-800 hover:border-primary hover:text-primary hover:bg-primary/5"}`}
                                                    >
                                                        <div className="flex items-center justify-center gap-1.5">
                                                            <Icon
                                                                value="plus"
                                                                size={14}
                                                                className="group-hover:scale-110 transition-transform"
                                                            />
                                                            <span>
                                                                {t(
                                                                    "tasks.sprints.empty_sprint"
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {sprintTasks.map(
                                                            (task, index) => (
                                                                <SprintTaskRow
                                                                    key={
                                                                        task.id
                                                                    }
                                                                    task={task}
                                                                    index={
                                                                        index
                                                                    }
                                                                    isSelected={selectedTaskIds.includes(
                                                                        task.id
                                                                    )}
                                                                    onToggleSelect={
                                                                        handleToggleSelectTask
                                                                    }
                                                                    showMoveSwitch={
                                                                        moveModeSection ===
                                                                        activeSprint.id
                                                                    }
                                                                    otherSprints={otherSprintsOptions(
                                                                        activeSprint.id
                                                                    )}
                                                                    onMove={(
                                                                        newSprintId
                                                                    ) =>
                                                                        handleMoveTaskDirect(
                                                                            task.id,
                                                                            newSprintId
                                                                        )
                                                                    }
                                                                    onClick={(
                                                                        t
                                                                    ) =>
                                                                        setSelectedTask(
                                                                            t
                                                                        )
                                                                    }
                                                                    onPriorityClick={(
                                                                        t
                                                                    ) =>
                                                                        setPriorityTarget(
                                                                            t
                                                                        )
                                                                    }
                                                                    onStatusClick={(
                                                                        t
                                                                    ) =>
                                                                        setStatusTarget(
                                                                            t
                                                                        )
                                                                    }
                                                                    onAssigneeClick={(
                                                                        t
                                                                    ) =>
                                                                        setAssigneeTarget(
                                                                            t
                                                                        )
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </Accordion>
                            );
                        })()
                    ) : (
                        <div className="bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/80 dark:border-amber-900/40 rounded-2xl p-5 flex items-start gap-3.5">
                            <Icon
                                value="alertCircle"
                                size={20}
                                className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
                            />
                            <div>
                                <h4 className="font-bold text-amber-900 dark:text-amber-300 text-sm">
                                    {t("tasks.sprints.no_active")}
                                </h4>
                                <p className="text-xs text-amber-700/90 dark:text-amber-400/80 mt-1">
                                    {t("tasks.sprints.no_active_desc")}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Planning / Future Sprints */}
                    <Droppable droppableId="sprints-list" type="sprint">
                        {(sprintsProvided) => (
                            <div
                                ref={sprintsProvided.innerRef}
                                {...sprintsProvided.droppableProps}
                            >
                                {planningOrder
                                    .map((id) =>
                                        planningSprints.find((s) => s.id === id)
                                    )
                                    .filter(
                                        (sprint): sprint is Sprint =>
                                            sprint !== undefined
                                    )
                                    .map((sprint, sprintIndex) => {
                                        const sprintTasks =
                                            getFilteredSectionTasks(sprint.id);
                                        return (
                                            <Draggable
                                                key={sprint.id}
                                                draggableId={`sprint-${sprint.id}`}
                                                index={sprintIndex}
                                            >
                                                {(
                                                    dragProvided,
                                                    dragSnapshot
                                                ) => (
                                                    <div
                                                        ref={
                                                            dragProvided.innerRef
                                                        }
                                                        {...dragProvided.draggableProps}
                                                        className={`relative group/sprint mb-4 transition-all ${dragSnapshot.isDragging ? "opacity-90 shadow-2xl z-50" : ""}`}
                                                    >
                                                        {/* Grip handle overlaid on left border at middle height of entire accordion */}
                                                        <div
                                                            {...dragProvided.dragHandleProps}
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                            className="absolute -left-3.5 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center cursor-grab active:cursor-grabbing text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary transition-all duration-150 hover:scale-110"
                                                            title={t(
                                                                "tasks.sprints.drag_to_reorder"
                                                            )}
                                                        >
                                                            <Icon
                                                                value="gripVertical"
                                                                size={28}
                                                            />
                                                        </div>

                                                        <Accordion
                                                            key={sprint.id}
                                                            expanded={
                                                                expandedSections[
                                                                    sprint.id
                                                                ] !== undefined
                                                                    ? expandedSections[
                                                                          sprint
                                                                              .id
                                                                      ]
                                                                    : true
                                                            }
                                                            onChange={(
                                                                expanded
                                                            ) => {
                                                                setExpandedSections(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [sprint.id]:
                                                                            expanded,
                                                                    })
                                                                );
                                                            }}
                                                            icon={
                                                                <div className="flex items-center gap-2">
                                                                    <Badge
                                                                        variant="primary"
                                                                        count
                                                                        value={String(
                                                                            sprintTasks.length
                                                                        )}
                                                                    />
                                                                    <Icon
                                                                        value="inbox"
                                                                        size={
                                                                            18
                                                                        }
                                                                        className="text-primary"
                                                                    />
                                                                </div>
                                                            }
                                                            className="mb-4"
                                                            title={
                                                                <div className="flex items-center gap-2">
                                                                    <span>
                                                                        {
                                                                            sprint.name
                                                                        }
                                                                    </span>
                                                                    {sprint.goal ? (
                                                                        <span className="text-xs italic text-slate-400 dark:text-zinc-500 border-l border-slate-200 dark:border-zinc-700 pl-3 font-normal">
                                                                            {
                                                                                sprint.goal
                                                                            }
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                            }
                                                            subtitle={
                                                                formatDates(
                                                                    sprint.start_date,
                                                                    sprint.end_date
                                                                ) ? (
                                                                    <Badge
                                                                        variant="secondary"
                                                                        icon="calendar"
                                                                        value={formatDates(
                                                                            sprint.start_date,
                                                                            sprint.end_date
                                                                        )}
                                                                    />
                                                                ) : undefined
                                                            }
                                                            actions={
                                                                <div className="flex items-center gap-2">
                                                                    <Button
                                                                        variant="primary"
                                                                        icon="play"
                                                                        tooltip={t(
                                                                            "tasks.sprints.start"
                                                                        )}
                                                                        onClick={() =>
                                                                            handleOpenStartModal(
                                                                                sprint
                                                                            )
                                                                        }
                                                                        className="w-9 h-9"
                                                                    />

                                                                    <Button
                                                                        variant="basic"
                                                                        icon="plus"
                                                                        tooltip={t(
                                                                            "tasks.sprints.add_task"
                                                                        )}
                                                                        onClick={() =>
                                                                            handleOpenCreateTask(
                                                                                sprint.id
                                                                            )
                                                                        }
                                                                        className="text-slate-400! hover:text-primary! w-9 h-9"
                                                                    />

                                                                    <SprintActionsPopover
                                                                        sprint={
                                                                            sprint
                                                                        }
                                                                        onEdit={
                                                                            handleOpenEditModal
                                                                        }
                                                                        onDelete={
                                                                            handleDeleteSprint
                                                                        }
                                                                        onMoveTasks={
                                                                            sprintTasks.length >
                                                                            0
                                                                                ? () =>
                                                                                      handleToggleMoveMode(
                                                                                          sprint.id
                                                                                      )
                                                                                : undefined
                                                                        }
                                                                        isMoving={
                                                                            moveModeSection ===
                                                                            sprint.id
                                                                        }
                                                                    />
                                                                </div>
                                                            }
                                                        >
                                                            <Droppable
                                                                droppableId={String(
                                                                    sprint.id
                                                                )}
                                                            >
                                                                {(
                                                                    provided,
                                                                    snapshot
                                                                ) => (
                                                                    <div
                                                                        ref={
                                                                            provided.innerRef
                                                                        }
                                                                        {...provided.droppableProps}
                                                                        className={`px-5 pt-2 pb-5 min-h-[80px] transition-colors`}
                                                                    >
                                                                        {renderMoveRow(
                                                                            sprint.id,
                                                                            sprintTasks
                                                                        )}

                                                                        {sprintTasks.length ===
                                                                        0 ? (
                                                                            <div
                                                                                onClick={() =>
                                                                                    handleOpenCreateTask(
                                                                                        sprint.id
                                                                                    )
                                                                                }
                                                                                className={`text-center py-5 text-xs border border-dashed rounded-xl transition-colors cursor-pointer group ${snapshot.isDraggingOver ? "border-primary bg-primary/5 text-primary" : "text-slate-400 dark:text-zinc-500 border-slate-200 dark:border-zinc-800 hover:border-primary hover:text-primary hover:bg-primary/5"}`}
                                                                            >
                                                                                <div className="flex items-center justify-center gap-1.5">
                                                                                    <Icon
                                                                                        value="plus"
                                                                                        size={
                                                                                            14
                                                                                        }
                                                                                        className="group-hover:scale-110 transition-transform"
                                                                                    />
                                                                                    <span>
                                                                                        {t(
                                                                                            "tasks.sprints.empty_sprint"
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="space-y-2">
                                                                                {sprintTasks.map(
                                                                                    (
                                                                                        task,
                                                                                        index
                                                                                    ) => (
                                                                                        <SprintTaskRow
                                                                                            key={
                                                                                                task.id
                                                                                            }
                                                                                            task={
                                                                                                task
                                                                                            }
                                                                                            index={
                                                                                                index
                                                                                            }
                                                                                            isSelected={selectedTaskIds.includes(
                                                                                                task.id
                                                                                            )}
                                                                                            onToggleSelect={
                                                                                                handleToggleSelectTask
                                                                                            }
                                                                                            showMoveSwitch={
                                                                                                moveModeSection ===
                                                                                                sprint.id
                                                                                            }
                                                                                            otherSprints={otherSprintsOptions(
                                                                                                sprint.id
                                                                                            )}
                                                                                            onMove={(
                                                                                                newSprintId
                                                                                            ) =>
                                                                                                handleMoveTaskDirect(
                                                                                                    task.id,
                                                                                                    newSprintId
                                                                                                )
                                                                                            }
                                                                                            onClick={(
                                                                                                t
                                                                                            ) =>
                                                                                                setSelectedTask(
                                                                                                    t
                                                                                                )
                                                                                            }
                                                                                            onPriorityClick={(
                                                                                                t
                                                                                            ) =>
                                                                                                setPriorityTarget(
                                                                                                    t
                                                                                                )
                                                                                            }
                                                                                            onStatusClick={(
                                                                                                t
                                                                                            ) =>
                                                                                                setStatusTarget(
                                                                                                    t
                                                                                                )
                                                                                            }
                                                                                            onAssigneeClick={(
                                                                                                t
                                                                                            ) =>
                                                                                                setAssigneeTarget(
                                                                                                    t
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {
                                                                            provided.placeholder
                                                                        }
                                                                    </div>
                                                                )}
                                                            </Droppable>
                                                        </Accordion>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                {sprintsProvided.placeholder}
                            </div>
                        )}
                    </Droppable>

                    {/* Backlog Section */}
                    <Accordion
                        expanded={
                            expandedSections["backlog"] !== undefined
                                ? expandedSections["backlog"]
                                : true
                        }
                        onChange={(expanded) => {
                            setExpandedSections((prev) => ({
                                ...prev,
                                backlog: expanded,
                            }));
                        }}
                        icon={
                            <div className="flex items-center gap-2">
                                <Badge
                                    variant="primary"
                                    count
                                    value={String(backlogTasks.length)}
                                />
                                <Icon
                                    value="inbox"
                                    size={18}
                                    className="text-primary"
                                />
                            </div>
                        }
                        title={t("tasks.sprints.backlog")}
                        actions={
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="basic"
                                    icon="plus"
                                    tooltip={t("tasks.sprints.add_task")}
                                    onClick={() => handleOpenCreateTask(null)}
                                    className="text-slate-400! hover:text-primary! w-9 h-9"
                                />

                                <BacklogActionsPopover
                                    onMoveTasks={() =>
                                        handleToggleMoveMode("backlog")
                                    }
                                    isMoving={moveModeSection === "backlog"}
                                />
                            </div>
                        }
                    >
                        <Droppable droppableId="backlog">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`px-5 pt-2 pb-5 min-h-[100px] transition-colors`}
                                >
                                    {renderMoveRow(null, backlogTasks)}

                                    {backlogTasks.length === 0 ? (
                                        <div
                                            onClick={() =>
                                                handleOpenCreateTask(null)
                                            }
                                            className={`text-center py-8 text-xs border border-dashed rounded-xl transition-colors cursor-pointer group ${snapshot.isDraggingOver ? "border-primary bg-primary/5 text-primary" : "text-slate-400 dark:text-zinc-500 border-slate-200 dark:border-zinc-800 hover:border-primary hover:text-primary hover:bg-primary/5"}`}
                                        >
                                            <div className="flex items-center justify-center gap-1.5">
                                                <Icon
                                                    value="plus"
                                                    size={14}
                                                    className="group-hover:scale-110 transition-transform"
                                                />
                                                <span>
                                                    {t(
                                                        "tasks.sprints.empty_backlog"
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {backlogTasks.map((task, index) => (
                                                <SprintTaskRow
                                                    key={task.id}
                                                    task={task}
                                                    index={index}
                                                    isSelected={selectedTaskIds.includes(
                                                        task.id
                                                    )}
                                                    onToggleSelect={
                                                        handleToggleSelectTask
                                                    }
                                                    showMoveSwitch={
                                                        moveModeSection ===
                                                        "backlog"
                                                    }
                                                    otherSprints={otherSprintsOptions(
                                                        null
                                                    )}
                                                    onMove={(newSprintId) =>
                                                        handleMoveTaskDirect(
                                                            task.id,
                                                            newSprintId
                                                        )
                                                    }
                                                    onClick={(t) =>
                                                        setSelectedTask(t)
                                                    }
                                                    onPriorityClick={(t) =>
                                                        setPriorityTarget(t)
                                                    }
                                                    onStatusClick={(t) =>
                                                        setStatusTarget(t)
                                                    }
                                                    onAssigneeClick={(t) =>
                                                        setAssigneeTarget(t)
                                                    }
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </Accordion>
                </DragDropContext>
            )}

            {/* Create / Edit Sprint Modal */}
            <Modal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                title={
                    editingSprint
                        ? t("tasks.sprints.create_modal.edit_title")
                        : t("tasks.sprints.create_modal.title")
                }
                onSubmit={handleSaveSprint}
                submitLabel={t("tasks.sprints.create_modal.submit")}
                cancelLabel={t("tasks.sprints.create_modal.cancel")}
                isSubmitting={isSubmitting}
                hideFullscreen
                maxWidth="sm"
            >
                <div className="space-y-4 py-2">
                    <Input
                        label={t("tasks.sprints.name")}
                        value={sprintFormData.name}
                        onChange={(e) =>
                            setSprintFormData((prev) => ({
                                ...prev,
                                name: e.target.value,
                            }))
                        }
                        required
                    />

                    <div>
                        <label className="text-xs font-bold text-slate-700 dark:text-zinc-300 block mb-1">
                            {t("tasks.sprints.start_date")}
                        </label>
                        <DatePicker
                            full
                            placeholder={t("tasks.sprints.start_date")}
                            value={sprintFormData.start_date || null}
                            onChange={(val) =>
                                setSprintFormData((prev) => ({
                                    ...prev,
                                    start_date: val ?? "",
                                }))
                            }
                        />
                    </div>

                    <div>
                        <label className="text-xs font-bold text-slate-700 dark:text-zinc-300 block mb-1">
                            {t("tasks.sprints.end_date")}
                        </label>
                        <DatePicker
                            full
                            placeholder={t("tasks.sprints.end_date")}
                            value={sprintFormData.end_date || null}
                            onChange={(val) => {
                                const newEnd = val ?? "";
                                if (newEnd && !sprintFormData.start_date) {
                                    showError(
                                        t("tasks.sprints.end_date_no_start")
                                    );
                                    return;
                                }
                                if (
                                    newEnd &&
                                    sprintFormData.start_date &&
                                    newEnd < sprintFormData.start_date
                                ) {
                                    showError(
                                        t("tasks.sprints.end_date_before_start")
                                    );
                                    return;
                                }
                                setSprintFormData((prev) => ({
                                    ...prev,
                                    end_date: newEnd,
                                }));
                            }}
                        />
                    </div>

                    <Textarea
                        label={t("tasks.sprints.goal")}
                        value={sprintFormData.goal}
                        onChange={(e) =>
                            setSprintFormData((prev) => ({
                                ...prev,
                                goal: e.target.value,
                            }))
                        }
                        rows={3}
                    />
                </div>
            </Modal>

            {/* Start Sprint Modal */}
            <Modal
                isOpen={!!startSprintTarget}
                onClose={() => setStartSprintTarget(null)}
                title={t("tasks.sprints.start_modal.title")}
                onSubmit={handleConfirmStart}
                submitLabel={t("tasks.sprints.start")}
                cancelLabel={t("common.cancel")}
                isSubmitting={isSubmitting}
                hideFullscreen
                maxWidth="md"
            >
                <div className="space-y-4 py-2">
                    <div>
                        <label className="text-xs font-bold text-slate-700 dark:text-zinc-300 block mb-1">
                            {t("tasks.sprints.start_date")}{" "}
                            <span className="text-primary">*</span>
                        </label>
                        <DatePicker
                            full
                            placeholder={t("tasks.sprints.start_date")}
                            value={startFormData.start_date || null}
                            error={startFormErrors.start_date}
                            onChange={(val) => {
                                setStartFormData((prev) => ({
                                    ...prev,
                                    start_date: val ?? "",
                                }));
                                if (val) {
                                    setStartFormErrors((prev) => ({
                                        ...prev,
                                        start_date: undefined,
                                    }));
                                }
                            }}
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-700 dark:text-zinc-300 block mb-1">
                            {t("tasks.sprints.end_date")}{" "}
                            <span className="text-primary">*</span>
                        </label>
                        <DatePicker
                            full
                            placeholder={t("tasks.sprints.end_date")}
                            value={startFormData.end_date || null}
                            error={startFormErrors.end_date}
                            onChange={(val) => {
                                const newEnd = val ?? "";
                                setStartFormData((prev) => ({
                                    ...prev,
                                    end_date: newEnd,
                                }));
                                if (
                                    newEnd &&
                                    startFormData.start_date &&
                                    newEnd >= startFormData.start_date
                                ) {
                                    setStartFormErrors((prev) => ({
                                        ...prev,
                                        end_date: undefined,
                                    }));
                                } else if (
                                    newEnd &&
                                    !startFormData.start_date
                                ) {
                                    setStartFormErrors((prev) => ({
                                        ...prev,
                                        end_date: undefined,
                                    }));
                                }
                            }}
                        />
                    </div>
                    <Textarea
                        label={t("tasks.sprints.goal")}
                        value={startFormData.goal}
                        onChange={(e) =>
                            setStartFormData((prev) => ({
                                ...prev,
                                goal: e.target.value,
                            }))
                        }
                        rows={5}
                    />
                </div>
            </Modal>

            {/* Complete Sprint Modal */}
            <Modal
                isOpen={!!completeSprintTarget}
                onClose={() => setCompleteSprintTarget(null)}
                title={t("tasks.sprints.complete_modal.title")}
                onSubmit={handleConfirmComplete}
                submitLabel={t("tasks.sprints.complete")}
                cancelLabel={t("common.cancel")}
                isSubmitting={isSubmitting}
                hideFullscreen
                maxWidth="sm"
            >
                <div className="py-2">
                    {(() => {
                        const currentSprintTasks = tasks.filter(
                            (t) => t.sprint_id === completeSprintTarget?.id
                        );
                        const completedCount = currentSprintTasks.filter(
                            (t) => t.status?.tag === "done"
                        ).length;
                        const uncompletedCount =
                            currentSprintTasks.length - completedCount;

                        const moveOptions = [
                            {
                                value: "backlog",
                                label: t("tasks.sprints.backlog"),
                            },
                            ...sprints
                                .filter(
                                    (s) =>
                                        s.id !== completeSprintTarget?.id &&
                                        s.status !== "completed"
                                )
                                .map((s) => ({
                                    value: String(s.id),
                                    label: s.name,
                                })),
                        ];

                        return (
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 rounded-xl p-3 text-center">
                                        <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                                            {completedCount}
                                        </div>
                                        <div className="text-xs text-emerald-700 dark:text-emerald-300 font-medium mt-1">
                                            {t(
                                                "tasks.sprints.complete_modal.completed_tasks"
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-xl p-3 text-center">
                                        <div className="text-2xl font-black text-amber-600 dark:text-amber-400">
                                            {uncompletedCount}
                                        </div>
                                        <div className="text-xs text-amber-700 dark:text-amber-300 font-medium mt-1">
                                            {t(
                                                "tasks.sprints.complete_modal.uncompleted_tasks"
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {uncompletedCount > 0 && (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-2">
                                            {t(
                                                "tasks.sprints.complete_modal.move_to_label"
                                            )}
                                        </label>
                                        <Select
                                            value={moveToSprintId}
                                            onChange={(val) =>
                                                setMoveToSprintId(String(val))
                                            }
                                            options={moveOptions}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </Modal>

            {/* Delete Sprint Modal */}
            <Modal
                isOpen={!!sprintToDelete}
                onClose={() => setSprintToDelete(null)}
                title={t("tasks.sprints.delete")}
                submitVariant="danger"
                submitIcon={<Icon value="trash" size={16} />}
                onSubmit={confirmDeleteSprint}
                submitLabel={t("common.delete")}
                cancelLabel={t("common.cancel")}
                isSubmitting={isSubmitting}
                hideFullscreen
                maxWidth="sm"
            >
                <div className="py-2">
                    <div className="flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center shrink-0">
                            <Icon
                                value="trash"
                                className="w-6 h-6 text-rose-600 dark:text-rose-500"
                            />
                        </div>
                    </div>
                    <div className="text-center mt-1">
                        <div className="text-sm text-slate-900 dark:text-white">
                            {t("tasks.sprints.confirm_delete")}
                        </div>
                        <div className="font-bold mt-4">
                            {sprintToDelete?.name}
                        </div>
                    </div>
                </div>
            </Modal>

            <NewTaskModal
                isOpen={isNewTaskModalOpen}
                onClose={() => setIsNewTaskModalOpen(false)}
                onSuccess={handleTaskCreated}
                statuses={statuses}
                prefilledSprintId={createTaskSprintId}
            />

            <TaskViewModal
                task={selectedTask}
                isOpen={!!selectedTask}
                onClose={(hasChanged) => {
                    setSelectedTask(null);
                    if (hasChanged) {
                        loadData(false);
                        if (onSprintChanged) {
                            onSprintChanged();
                        }
                    }
                }}
            />

            <ChangeTaskPriorityModal
                isOpen={priorityTarget !== null}
                task={priorityTarget}
                onClose={() => setPriorityTarget(null)}
                onSuccess={handleTaskAttributeUpdated}
            />

            <ChangeTaskStatusModal
                isOpen={statusTarget !== null}
                task={statusTarget}
                onClose={() => setStatusTarget(null)}
                onSuccess={handleTaskAttributeUpdated}
            />

            <ChangeTaskAssigneeModal
                isOpen={assigneeTarget !== null}
                task={assigneeTarget}
                onClose={() => setAssigneeTarget(null)}
                onSuccess={handleTaskAttributeUpdated}
            />
        </div>
    );
};

interface SprintTaskRowProps {
    task: TaskItem;
    index: number;
    isSelected: boolean;
    onToggleSelect: (taskId: number) => void;
    showMoveSwitch?: boolean;
    otherSprints: { value: string; label: string }[];
    onMove: (newSprintId: number | null) => void;
    onClick?: (task: TaskItem) => void;
    onPriorityClick?: (task: TaskItem) => void;
    onStatusClick?: (task: TaskItem) => void;
    onAssigneeClick?: (task: TaskItem) => void;
}

const SprintTaskRow: React.FC<SprintTaskRowProps> = ({
    task,
    index,
    isSelected,
    onToggleSelect,
    showMoveSwitch = false,
    otherSprints,
    onMove,
    onClick,
    onPriorityClick,
    onStatusClick,
    onAssigneeClick,
}) => {
    const { t } = useTranslation("global");

    const priorityKey = task.priority ?? 2;
    const pConfig = PRIORITY_CONFIG[priorityKey] || PRIORITY_CONFIG[2];

    return (
        <Draggable draggableId={String(task.id)} index={index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={() => {
                        if (onClick) {
                            onClick(task);
                        }
                    }}
                    className={`flex items-center justify-between gap-3 p-3 rounded-xl border cursor-pointer active:cursor-grabbing transition-all ${
                        snapshot.isDragging
                            ? "bg-white dark:bg-zinc-800 border-primary ring-2 ring-primary/20 shadow-md scale-[1.01]"
                            : isSelected
                              ? "bg-primary/5 dark:bg-primary/10 border-primary/50 shadow-2xs"
                              : "bg-white dark:bg-zinc-800/90 border-slate-200/80 dark:border-zinc-700/60 hover:border-primary/40 shadow-2xs"
                    }`}
                >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        {showMoveSwitch && (
                            <div
                                className="shrink-0"
                                onClick={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <Switch
                                    checked={isSelected}
                                    onChange={() => onToggleSelect(task.id)}
                                    size="sm"
                                />
                            </div>
                        )}

                        {otherSprints.length > 0 && (
                            <MoveTaskDropdown
                                options={otherSprints}
                                onSelect={onMove}
                            />
                        )}

                        <Badge value={task.code} />

                        <span className="font-bold text-sm text-slate-800 dark:text-zinc-100 truncate">
                            {task.title}
                        </span>

                        {task?.description && task.description?.length > 0 && (
                            <span className="text-xs text-slate-500 dark:text-zinc-400 truncate hidden sm:inline">
                                {task.description}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        {pConfig && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onPriorityClick) {
                                        onPriorityClick(task);
                                    }
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                                className="p-1 rounded-lg transition-transform hover:scale-105 cursor-pointer"
                            >
                                <Tooltip
                                    title={t(`priorities.${pConfig.tag}`)}
                                    placement="top"
                                >
                                    <div className="flex items-center gap-1.5">
                                        <span
                                            className={`flex items-center -space-x-0.5 ${pConfig.color}`}
                                        >
                                            {Array.from({
                                                length: pConfig.arrows,
                                            }).map((_, idx) => (
                                                <Icon
                                                    key={idx}
                                                    value="arrowUp"
                                                    size={16}
                                                    strokeWidth={3}
                                                />
                                            ))}
                                        </span>
                                    </div>
                                </Tooltip>
                            </button>
                        )}

                        {task.status && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onStatusClick) {
                                        onStatusClick(task);
                                    }
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                                className="cursor-pointer transition-transform hover:scale-105"
                            >
                                <Badge
                                    className="w-28 text-center justify-center cursor-pointer"
                                    variant={
                                        TASK_STATUS_VARIANT[
                                            task.status.tag ?? ""
                                        ] ?? "neutral"
                                    }
                                    value={
                                        t(
                                            `tasks.statuses.${task.status.tag || "todo"}`,
                                            { defaultValue: task.status.name || t("tasks.statuses.todo") }
                                        )
                                    }
                                />
                            </div>
                        )}

                        <Tooltip
                            title={
                                task.assignee
                                    ? task.assignee.name
                                    : t("tasks.table.unassigned")
                            }
                            placement="top"
                        >
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onAssigneeClick) {
                                        onAssigneeClick(task);
                                    }
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                                className="cursor-pointer rounded-full transition-transform hover:scale-110 hover:ring-2 hover:ring-primary/40"
                            >
                                {task.assignee ? (
                                    task.assignee.avatar ||
                                    task.assignee.photo ? (
                                        <img
                                            src={
                                                task.assignee.avatar ||
                                                task.assignee.photo
                                            }
                                            alt={task.assignee.name}
                                            className="w-8 h-8 rounded-full object-cover shrink-0 ring-1 ring-slate-200 dark:ring-zinc-700"
                                        />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-primary text-white font-bold text-[10px] flex items-center justify-center shrink-0 shadow-2xs">
                                            {getAvatar(task.assignee.name)}
                                        </div>
                                    )
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-zinc-800 text-slate-400 dark:text-zinc-500 border border-dashed border-slate-300 dark:border-zinc-700 flex items-center justify-center shrink-0">
                                        <Icon value="user" size={16} />
                                    </div>
                                )}
                            </button>
                        </Tooltip>
                    </div>
                </div>
            )}
        </Draggable>
    );
};

export default SprintsSettings;
